/* Editorial shell and identity-only saved products. Same storage contract as GuildCommerce.
   On shopping pages GuildCommerce loads first and owns saving, comparison and counts; this
   script then only runs the header dialogs, so a Save click is never handled twice. */
(() => {
  'use strict';
  if (window.GuildEditorial) return;
  const key = 'collectors-guild:products:v1';
  const records = new Map();
  const dialogTriggers = new WeakMap();
  const mountedDialogs = new WeakSet();
  let state = { saved: [], compared: [] };
  let persistent = true;
  let priorOverflow = null;
  const commerce = () => Boolean(window.GuildCommerce);
  const validId = value => /^\d+$/.test(String(value || ''));
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value);
  const references = (items, limit) => {
    const seen = new Set();
    return (Array.isArray(items) ? items : []).filter(item => {
      if (!validId(item?.id) || !validHandle(item?.handle) || seen.has(String(item.id))) return false;
      seen.add(String(item.id)); return true;
    }).slice(0, limit).map(item => ({ id: String(item.id), handle: item.handle, variantId: validId(item.variantId) ? String(item.variantId) : '' }));
  };
  function readStorage() {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '{}');
      state = { saved: references(value?.saved, 40), compared: references(value?.compared, 3) };
      persistent = true;
    } catch { persistent = false; }
  }
  function persist() {
    try { localStorage.setItem(key, JSON.stringify(state)); persistent = true; }
    catch { persistent = false; }
  }
  function readProducts(scope = document) {
    scope.querySelectorAll('script[data-guild-product]').forEach(node => {
      try {
        const product = JSON.parse(node.textContent);
        if (validId(product?.id) && validHandle(product?.handle)) records.set(String(product.id), product);
      } catch { /* A malformed record does not enable actions for that product. */ }
    });
  }
  function announce(message) {
    const region = document.getElementById('cg-e-status');
    if (region) region.textContent = message;
  }
  function renderState() {
    for (const [attribute, list, label] of [['data-guild-save', 'saved', 'Save'], ['data-guild-compare', 'compared', 'Compare']]) {
      document.querySelectorAll(`button[${attribute}]`).forEach(button => {
        const id = button.getAttribute(attribute), active = state[list].some(ref => ref.id === id);
        button.hidden = !records.has(id);
        button.setAttribute('aria-pressed', String(active));
        const verb = active ? (list === 'saved' ? 'Unsave' : 'Stop comparing') : label;
        button.setAttribute('aria-label', `${verb} ${button.dataset.productTitle || records.get(id)?.title || 'product'}`);
        const text = button.querySelector(list === 'saved' ? '[data-cg-save-label]' : '[data-cg-compare-label]');
        const symbol = button.querySelector(list === 'saved' ? '[data-cg-save-symbol]' : '[data-cg-compare-symbol]');
        if (text) text.textContent = active ? (list === 'saved' ? 'Saved' : 'Selected') : label;
        if (symbol) symbol.textContent = active ? '✓' : '+';
      });
    }
    document.querySelectorAll('[data-guild-saved-count]').forEach(node => { node.textContent = String(state.saved.length); });
    document.querySelectorAll('[data-cg-compare-count]').forEach(node => { node.textContent = `(${state.compared.length})`; });
    document.querySelectorAll('[data-cg-compare-link]').forEach(node => { node.hidden = state.compared.length === 0; });
  }
  function toggleProduct(button, list, attribute) {
    const id = button.getAttribute(attribute), existing = state[list].some(ref => ref.id === id);
    if (existing) state[list] = state[list].filter(ref => ref.id !== id);
    else {
      const product = records.get(id);
      if (!product) return announce('Open the product page to check its current details.');
      const limit = list === 'saved' ? 40 : 3;
      if (state[list].length >= limit) return announce(list === 'saved' ? 'Your saved list is full. Remove an item to save another.' : 'Compare up to three products. Remove one selection to add another.');
      const previous = state.saved.find(ref => ref.id === id) || state.compared.find(ref => ref.id === id);
      const variantId = validId(product.variantId) ? String(product.variantId) : previous?.variantId || (product.variants?.length === 1 && validId(product.variants[0].id) ? String(product.variants[0].id) : '');
      state[list].push({ id, handle: product.handle, variantId });
    }
    persist(); renderState();
    // The hero's character reacts to a newly saved product (guild-editorial-characters.js).
    if (!existing && list === 'saved') document.dispatchEvent(new CustomEvent('cg:saved', { detail: { id } }));
    if (!persistent) return announce('Browser storage is unavailable. This change is kept on this page only and does not reserve stock.');
    announce(existing ? (list === 'saved' ? 'Removed from saved items.' : 'Removed from comparison.') : list === 'saved' ? 'Saved in this browser. Saving does not reserve stock.' : 'Added to comparison. Use Compare selected products to view your choices.');
  }
  function syncDialogScroll() {
    const open = document.querySelector('dialog[id^="cg-e-"][open]');
    if (open && priorOverflow === null) { priorOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; }
    else if (!open && priorOverflow !== null) { document.documentElement.style.overflow = priorOverflow; priorOverflow = null; }
  }
  function mountDialogs(scope = document) {
    scope.querySelectorAll('dialog[id^="cg-e-"]').forEach(dialog => {
      if (mountedDialogs.has(dialog)) return;
      mountedDialogs.add(dialog);
      dialog.addEventListener('close', () => {
        const trigger = dialogTriggers.get(dialog);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        syncDialogScroll();
        if (dialog.returnValue !== 'switch' && trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
    });
  }
  function openDialog(trigger) {
    const dialog = document.getElementById(trigger.dataset.cgOpen);
    if (!(dialog instanceof HTMLDialogElement)) return;
    document.querySelectorAll('dialog[id^="cg-e-"][open]').forEach(other => { if (other !== dialog) other.close('switch'); });
    dialog.hidden = false; dialog.returnValue = ''; dialogTriggers.set(dialog, trigger);
    if (!dialog.open) dialog.showModal();
    trigger.setAttribute('aria-expanded', 'true'); syncDialogScroll();
    const focus = dialog.querySelector('[autofocus]') || dialog.querySelector('input[type="search"]') || dialog.querySelector('[data-cg-close], a[href], button');
    focus?.focus({ preventScroll: true });
  }
  function mount(scope = document) { mountDialogs(scope); if (commerce()) return; readProducts(scope); renderState(); }
  function init() {
    if (!commerce()) readStorage();
    mount();
    document.addEventListener('click', event => {
      const open = event.target.closest('[data-cg-open]');
      if (open) { event.preventDefault(); openDialog(open); return; }
      const close = event.target.closest('[data-cg-close]');
      if (close) { event.preventDefault(); close.closest('dialog')?.close(); return; }
      const button = commerce() ? null : event.target.closest('button[data-guild-save], button[data-guild-compare]');
      if (!button) return;
      if (button.hasAttribute('data-guild-save')) toggleProduct(button, 'saved', 'data-guild-save');
      else toggleProduct(button, 'compared', 'data-guild-compare');
    });
    window.addEventListener('storage', event => { if (!commerce() && (event.key === key || event.key === null)) { readStorage(); renderState(); } });
    window.addEventListener('pageshow', event => { if (!commerce() && event.persisted) { readStorage(); renderState(); } });
    document.addEventListener('shopify:section:load', event => mount(event.target));
    document.addEventListener('guild:products:updated', event => mount(event.target));
  }
  window.GuildEditorial = { refresh: () => { readStorage(); mount(); } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
